import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Icon,
  Link,
  Popover,
  PropertyFilterProps,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  ClassificationEntry,
  ComparisonEnvironment,
  ShadowTestDiscrepancy,
  ShadowTestLine,
  ShadowTestMatchSet,
  ShadowTestResultResponse,
  Stage,
} from "../../lusca-search-api/generated-src";
import { createFlashUIMatcherLink } from "../../helpers/linkUtils";
import { COUNTRY_TO_MARKETPLACE } from "../../helpers/marketplaceUtils";
import {
  BundleAwareTable,
  numericFieldComparator,
} from "./shadowTestTableUtils";
import {
  comparisonEnvironmentLabel,
  relabelComparisonEnvironment,
} from "../../helpers/shadowTestComparisonEnvironment";

// Visually hidden but readable by screen readers / assistive tech.
const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// Presence tick for the usecase matrix: a green check when the usecase is booked in that
// environment, a muted dash when it is not. The environment is folded into a screen-reader label so
// the state is not conveyed by the icon alone.
const PresenceTick = ({ present, env }: { present: boolean; env: string }) =>
  present ? (
    <>
      <Icon name="status-positive" variant="success" />
      <span style={SR_ONLY}>in {env}</span>
    </>
  ) : (
    <>
      <Box variant="span" color="text-status-inactive">
        —
      </Box>
      <span style={SR_ONLY}>not in {env}</span>
    </>
  );

// The backend encodes presence as a "+"-joined environment string (e.g. "gamma+prod"). Render it as
// one compact letter badge per environment (G / P / J), with the full names on hover (native title)
// so the letters aren't the only context. A native title rather than a Popover keeps this cheap —
// bundle orders can have hundreds of line rows, i.e. hundreds of Env cells. All badges are the same
// neutral blue — the letter, not the colour, is the meaning; green would misread as a passed verdict.
const ENV_ABBREV: Record<string, { letter: string; full: string }> = {
  gamma: { letter: "G", full: "Gamma" },
  beta: { letter: "B", full: "Beta" },
  prod: { letter: "P", full: "Prod" },
  jem: { letter: "J", full: "JEM" },
};
const ENV_BADGE_COLOR = "blue" as const;

const EnvironmentBadges = ({ environment }: { environment?: string }) => {
  const envs = (environment ?? "")
    .split("+")
    .map((env) => env.trim())
    .filter(Boolean);
  if (envs.length === 0) return <>-</>;

  const meta = (env: string) =>
    ENV_ABBREV[env.toLowerCase()] ?? {
      letter: env.slice(0, 1).toUpperCase(),
      full: env,
    };
  const fullNames = envs.map((env) => meta(env).full).join(", ");

  return (
    <span
      title={fullNames}
      style={{
        whiteSpace: "nowrap",
        userSelect: "none",
        WebkitUserSelect: "none",
        cursor: "help",
      }}
    >
      {envs.map((env, index) => (
        <span
          key={env}
          style={{ marginRight: index < envs.length - 1 ? 2 : 0 }}
        >
          <Badge color={ENV_BADGE_COLOR}>{meta(env).letter}</Badge>
          {/* Full name for screen readers, since the native title tooltip isn't announced. */}
          <span style={SR_ONLY}>{meta(env).full}</span>
        </span>
      ))}
    </span>
  );
};

interface UsecasePresenceRow {
  usecase: string;
  gamma: boolean;
  prod: boolean;
  jem: boolean;
}

const prodPresenceCell = (row: UsecasePresenceRow) => (
  <PresenceTick present={row.prod} env="Prod" />
);

const jemPresenceCell = (row: UsecasePresenceRow) => (
  <PresenceTick present={row.jem} env="JEM" />
);

const usecasePresenceColumns = (
  hasJem: boolean,
  comparisonLabel: string
): TableProps.ColumnDefinition<UsecasePresenceRow>[] => [
  {
    id: "usecase",
    header: "Usecase",
    cell: (row) => row.usecase,
    width: 280,
  },
  {
    id: "gamma",
    header: comparisonLabel,
    cell: (row) => <PresenceTick present={row.gamma} env={comparisonLabel} />,
  },
  {
    id: "prod",
    header: "Prod",
    cell: prodPresenceCell,
  },
  ...(hasJem
    ? [
        {
          id: "jem",
          header: "JEM",
          cell: jemPresenceCell,
        },
      ]
    : []),
];

const checkStatusType = (
  passed: boolean | null | undefined
): "pending" | "success" | "error" => {
  if (passed == null) {
    return "pending";
  }
  return passed ? "success" : "error";
};

const checkStatusLabel = (label: string, passed: boolean | null | undefined) =>
  passed == null ? `${label} (Not run)` : label;

// A shadow test line plus an internal matched/unique tag. matchStatus is NOT rendered as a column
// (the Environment column already conveys source, and presence is not a verdict); it exists only so
// the quick-filter segmented control can split the merged rows into Matched vs Unique.
export type LineWithStatus = ShadowTestLine & { matchStatus: string };

// Tags each line from one source array with its matched/unique status and guarantees an environment
// (falling back to the source's default when the backend left it unset, so the Environment column
// and any environment filter stay meaningful). Prefers the backend `type` field; falls back to the
// array the line came from. Returns [] for a missing/empty source array.
const STATUS_BY_TYPE: Record<string, string> = {
  MATCHED: "Matched",
  PROD_UNIQUE: "Prod only",
  JEM_UNIQUE: "JEM only",
};
export const withStatus = (
  lines: ShadowTestLine[] | undefined,
  defaultEnvironment: string,
  defaultStatus: string,
  comparisonEnvironment: ComparisonEnvironment = ComparisonEnvironment.Gamma
): LineWithStatus[] =>
  (lines ?? []).map((line) => {
    const comparisonLabel = comparisonEnvironmentLabel(comparisonEnvironment);
    return {
      ...line,
      environment: relabelComparisonEnvironment(
        line.environment || defaultEnvironment,
        comparisonEnvironment
      ),
      matchStatus:
        line.type === "GAMMA_UNIQUE"
          ? `${comparisonLabel} only`
          : (line.type && STATUS_BY_TYPE[line.type]) || defaultStatus,
    };
  });

const ClassificationCell = ({
  classification,
  passed,
  classifications,
}: {
  classification?: string;
  passed?: boolean;
  classifications: Record<string, ClassificationEntry>;
}) => {
  // StatusIndicator renders custom node content, so Cloudscape's wrapLines does not keep it on a
  // single line. Force nowrap here so the classification text scrolls with the table instead of
  // wrapping inside the cell.
  const indicator = (
    <span style={{ whiteSpace: "nowrap" }}>
      <StatusIndicator type={passed ? "success" : "error"}>
        {classification || "-"}
      </StatusIndicator>
    </span>
  );

  const desc = classification
    ? classifications[classification]?.description
    : undefined;
  if (!desc) return indicator;

  return (
    <Popover
      dismissButton={false}
      position="top"
      size="medium"
      triggerType="custom"
      content={<Box>{desc}</Box>}
    >
      <span
        style={{
          cursor: "help",
          borderBottom: "1px dotted",
          whiteSpace: "nowrap",
        }}
      >
        {indicator}
      </span>
    </Popover>
  );
};

const lineColumns = (
  classifications: Record<string, ClassificationEntry>,
  showReturnId: boolean
): TableProps.ColumnDefinition<ShadowTestLine>[] => [
  {
    id: "classification",
    header: "Classification",
    cell: (e: ShadowTestLine) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
    sortingField: "classification",
  },
  {
    id: "env",
    header: "Env",
    cell: (e: ShadowTestLine) => (
      <EnvironmentBadges environment={e.environment} />
    ),
    sortingField: "environment",
  },
  {
    id: "usecase",
    header: "Usecase",
    cell: (e: ShadowTestLine) => e.usecase || "-",
    sortingField: "usecase",
  },
  ...(showReturnId
    ? [
        {
          id: "returnId",
          header: "Return ID",
          cell: (e: ShadowTestLine) => e.returnId || "-",
          sortingField: "returnId",
        },
      ]
    : []),
  {
    id: "account",
    header: "Account Number",
    cell: (e: ShadowTestLine) => e.accountNumber || "-",
    sortingField: "accountNumber",
  },
  {
    id: "side",
    header: "Journal Side",
    cell: (e: ShadowTestLine) => e.journalSide || "-",
    sortingField: "journalSide",
  },
  {
    id: "companyCode",
    header: "Company Code",
    cell: (e: ShadowTestLine) => e.companyCode || "-",
    sortingField: "companyCode",
  },
  {
    id: "location",
    header: "Location",
    cell: (e: ShadowTestLine) => e.location || "-",
    sortingField: "location",
  },
  {
    id: "costCenter",
    header: "Cost Center",
    cell: (e: ShadowTestLine) => e.costCenter || "-",
    sortingField: "costCenter",
  },
  {
    id: "productLine",
    header: "Product Line",
    cell: (e: ShadowTestLine) => e.productLine || "-",
    sortingField: "productLine",
  },
  {
    id: "channel",
    header: "Channel",
    cell: (e: ShadowTestLine) => e.channel || "-",
    sortingField: "channel",
  },
  {
    id: "project",
    header: "Project",
    cell: (e: ShadowTestLine) => e.project || "-",
    sortingField: "project",
  },
  {
    id: "functionalAmount",
    header: "Functional",
    cell: (e: ShadowTestLine) =>
      e.functionalAmount
        ? `${e.functionalAmount} ${e.functionalCurrency || e.currency || ""}`
        : "-",
    sortingField: "functionalAmount",
    sortingComparator: numericFieldComparator(
      (e: ShadowTestLine) => e.functionalAmount
    ),
  },
  {
    id: "localAmount",
    header: "Local",
    cell: (e: ShadowTestLine) =>
      e.localAmount
        ? `${e.localAmount} ${e.localCurrency || e.currency || ""}`
        : "-",
    sortingField: "localAmount",
    sortingComparator: numericFieldComparator(
      (e: ShadowTestLine) => e.localAmount
    ),
  },
  {
    id: "description",
    header: "GL Line Description",
    cell: (e: ShadowTestLine) => e.glLineDescription || "-",
    sortingField: "glLineDescription",
  },
  {
    id: "setOfBooks",
    header: "Set of Books",
    cell: (e: ShadowTestLine) => e.setOfBooks || "-",
    sortingField: "setOfBooks",
  },
  {
    id: "matchKey",
    header: "Match Key",
    cell: (e: ShadowTestLine) => e.matchKey || "-",
    sortingField: "matchKey",
  },
  {
    id: "intent",
    header: "Intent",
    cell: (e: ShadowTestLine) => e.intent || "-",
    sortingField: "intent",
  },
];

// Default order the matched/unique tables load in: usecase first, then GL line description, both
// ascending. Users can re-sort by any column afterwards. In bundle mode this orders the lines
// within each GTI group, since the groups themselves keep their natural order.
const byUsecaseThenDescription = (a: ShadowTestLine, b: ShadowTestLine) =>
  (a.usecase ?? "").localeCompare(b.usecase ?? "") ||
  (a.glLineDescription ?? "").localeCompare(b.glLineDescription ?? "");

// Merged Matched + Unique table. Matched and unique lines share the same columns; the existing
// Environment column already distinguishes source (gamma+prod for matched, gamma/prod/jem for
// unique), so no dedicated status column is added — presence is not a verdict, and the only
// correctness signal stays in the Classification column. Reuses the shared line columns (typed for
// the wider LineWithStatus row, which is a superset of ShadowTestLine).
const UnifiedLineTable = ({
  isBundle,
  lines,
  classifications,
  filterActions,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  isBundle?: boolean;
  lines: LineWithStatus[];
  classifications: Record<string, ClassificationEntry>;
  filterActions?: React.ReactNode;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) => {
  const sortedLines = useMemo(
    () => [...lines].sort(byUsecaseThenDescription),
    [lines]
  );
  const columns = useMemo(
    () =>
      lineColumns(
        classifications,
        lines.some((line) => Boolean(line.returnId?.trim()))
      ) as TableProps.ColumnDefinition<LineWithStatus>[],
    [classifications, lines]
  );
  return (
    <div style={{ fontSize: "12px" }}>
      <BundleAwareTable
        isBundle={isBundle}
        items={sortedLines}
        columnDefinitions={columns}
        filterActions={filterActions}
        initialPropertyFilter={initialPropertyFilter}
        onPropertyFilterChange={onPropertyFilterChange}
      />
    </div>
  );
};

// Quick filters layered above the property filter. They are lenses, not flags: narrowing to unique
// lines does not imply anything is wrong (a line present in one environment is often expected).
// "Failing" is the actual broken-line lens, driven by the per-line pass/fail verdict. The per-column
// property filter and sorting still apply on top of the narrowed set.
type LineQuickFilter = "all" | "matched" | "unique" | "failing";
const quickFilterPredicate = (
  id: LineQuickFilter
): ((line: LineWithStatus) => boolean) => {
  switch (id) {
    case "matched":
      return (line) => line.matchStatus === "Matched";
    case "unique":
      return (line) => line.matchStatus !== "Matched";
    case "failing":
      return (line) => line.passed === false;
    default:
      return () => true;
  }
};

const VALID_LINE_QUICK_FILTERS: LineQuickFilter[] = [
  "all",
  "matched",
  "unique",
  "failing",
];

export const LinesTab = ({
  isBundle,
  lines,
  classifications,
  initialFilter,
  onFilterChange,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  isBundle?: boolean;
  lines: LineWithStatus[];
  classifications: Record<string, ClassificationEntry>;
  initialFilter?: string;
  onFilterChange?: (filter: string) => void;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) => {
  const [quickFilter, setQuickFilter] = useState<LineQuickFilter>(
    VALID_LINE_QUICK_FILTERS.includes(initialFilter as LineQuickFilter)
      ? (initialFilter as LineQuickFilter)
      : "all"
  );

  const handleFilterChange = useCallback(
    (filter: LineQuickFilter) => {
      setQuickFilter(filter);
      onFilterChange?.(filter);
    },
    [onFilterChange]
  );

  const counts = useMemo(
    () => ({
      all: lines.length,
      matched: lines.filter(quickFilterPredicate("matched")).length,
      unique: lines.filter(quickFilterPredicate("unique")).length,
      failing: lines.filter(quickFilterPredicate("failing")).length,
    }),
    [lines]
  );

  // If the selected quick filter no longer matches any rows (e.g. after a refresh the failing count
  // drops to 0 and its segment becomes disabled), fall back to "All" so the table can't get stuck
  // showing nothing with the active segment disabled.
  useEffect(() => {
    if (quickFilter !== "all" && counts[quickFilter] === 0) {
      handleFilterChange("all");
    }
  }, [counts, quickFilter, handleFilterChange]);

  const filteredLines = useMemo(
    () => lines.filter(quickFilterPredicate(quickFilter)),
    [lines, quickFilter]
  );

  const quickFilterControl = (
    <SegmentedControl
      selectedId={quickFilter}
      onChange={({ detail }) => {
        handleFilterChange(detail.selectedId as LineQuickFilter);
      }}
      label="Quick filter lines"
      options={[
        { id: "all", text: `All (${counts.all})` },
        { id: "matched", text: `Matched (${counts.matched})` },
        { id: "unique", text: `Unique (${counts.unique})` },
        {
          id: "failing",
          text: `Failing (${counts.failing})`,
          disabled: counts.failing === 0,
        },
      ]}
    />
  );

  return (
    <UnifiedLineTable
      isBundle={isBundle}
      lines={filteredLines}
      classifications={classifications}
      filterActions={quickFilterControl}
      initialPropertyFilter={initialPropertyFilter}
      onPropertyFilterChange={onPropertyFilterChange}
    />
  );
};

// Summary tab content. Currently the usecase presence matrix — each usecase appears once, ticked in
// the environments where it was booked. Structured as a stack of sections so it can grow into a
// checks rollup (financial validation, amount check, bundle integrity, ...) without reshuffling.
export const SummaryTab = ({
  result,
  comparisonEnvironment,
}: {
  result: ShadowTestResultResponse;
  comparisonEnvironment: ComparisonEnvironment;
}) => {
  const comparisonLabel = comparisonEnvironmentLabel(comparisonEnvironment);
  const usecaseRows = useMemo<UsecasePresenceRow[]>(() => {
    const gamma = new Set(result.gammaUsecases ?? []);
    const prod = new Set(result.prodUsecases ?? []);
    const jem = new Set(result.jemUsecases ?? []);
    return Array.from(new Set([...gamma, ...prod, ...jem]))
      .sort((a, b) => a.localeCompare(b))
      .map((usecase) => ({
        usecase,
        gamma: gamma.has(usecase),
        prod: prod.has(usecase),
        jem: jem.has(usecase),
      }));
  }, [result.gammaUsecases, result.prodUsecases, result.jemUsecases]);

  const hasJem = (result.jemUsecases?.length ?? 0) > 0;
  const columns = useMemo(
    () => usecasePresenceColumns(hasJem, comparisonLabel),
    [comparisonLabel, hasJem]
  );

  // Validation checks rollup. Overall and the source-amount check always list (a null value renders
  // "Not run" rather than being hidden, so the set is predictable); the bundle checks list only for
  // bundle orders, where they apply. Each is a colour + label StatusIndicator so the verdict is not
  // conveyed by colour alone.
  const checks: { label: string; passed?: boolean }[] = [
    { label: "Overall", passed: result.passed },
    { label: "Source amounts match", passed: result.sourceAmountCheckPassed },
    ...(result.isBundle
      ? [
          { label: "Bundle allocation", passed: result.bundleAllocationPassed },
          {
            label: "Payment integrity",
            passed: result.bundleAllocationPaymentIntegrityPassed,
          },
        ]
      : []),
  ];

  return (
    <SpaceBetween size="l">
      <SpaceBetween size="xs">
        <Box variant="h4">Checks</Box>
        <SpaceBetween size="xxs">
          {checks.map((check) => (
            <StatusIndicator
              key={check.label}
              type={checkStatusType(check.passed)}
            >
              {checkStatusLabel(check.label, check.passed)}
            </StatusIndicator>
          ))}
        </SpaceBetween>
      </SpaceBetween>
      {usecaseRows.length > 0 && (
        <SpaceBetween size="xs">
          <Box variant="h4">Usecases by environment</Box>
          <Table
            items={usecaseRows}
            columnDefinitions={columns}
            variant="embedded"
          />
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
};

const discrepancyColumns = (
  classifications: Record<string, ClassificationEntry>,
  comparisonLabel: string,
  showReturnId: boolean
): TableProps.ColumnDefinition<ShadowTestDiscrepancy>[] => [
  {
    id: "field",
    header: "Field",
    cell: (e) => e.field || "-",
    sortingField: "field",
    minWidth: 140,
  },
  {
    id: "usecase",
    header: "Usecase",
    cell: (e) => e.usecase || "-",
    sortingField: "usecase",
    minWidth: 160,
  },
  ...(showReturnId
    ? [
        {
          id: "returnId",
          header: "Return ID",
          cell: (e: ShadowTestDiscrepancy) => e.returnId || "-",
          sortingField: "returnId",
          minWidth: 160,
        },
      ]
    : []),
  {
    id: "gammaValue",
    header: comparisonLabel,
    cell: (e) => e.gammaValue || "-",
    sortingField: "gammaValue",
    minWidth: 160,
  },
  {
    id: "prodValue",
    header: "Prod",
    cell: (e) => e.prodValue || "-",
    sortingField: "prodValue",
    minWidth: 160,
  },
  {
    id: "jemValue",
    header: "JEM",
    cell: (e) => e.jemValue || "-",
    sortingField: "jemValue",
    minWidth: 160,
  },
  {
    id: "intent",
    header: "Intent",
    cell: (e) => e.intent || "-",
    sortingField: "intent",
    minWidth: 100,
  },
  {
    id: "account",
    header: "Account",
    cell: (e) => e.accountNumber || "-",
    sortingField: "accountNumber",
    minWidth: 140,
  },
  {
    id: "classification",
    header: "Classification",
    cell: (e) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
    sortingField: "classification",
    minWidth: 140,
  },
];

type DiscrepancyQuickFilter = "all" | "failing";
const discrepancyQuickFilterPredicate = (
  id: DiscrepancyQuickFilter
): ((discrepancy: ShadowTestDiscrepancy) => boolean) =>
  id === "failing" ? (discrepancy) => discrepancy.passed === false : () => true;

export const DiscrepancyTable = ({
  isBundle,
  discrepancies,
  classifications,
  comparisonLabel,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  isBundle?: boolean;
  discrepancies: ShadowTestDiscrepancy[];
  classifications: Record<string, ClassificationEntry>;
  comparisonLabel: string;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) => {
  const [quickFilter, setQuickFilter] = useState<DiscrepancyQuickFilter>("all");
  const failingCount = useMemo(
    () =>
      discrepancies.filter(discrepancyQuickFilterPredicate("failing")).length,
    [discrepancies]
  );

  useEffect(() => {
    if (quickFilter === "failing" && failingCount === 0) {
      setQuickFilter("all");
    }
  }, [failingCount, quickFilter]);

  const filteredDiscrepancies = useMemo(
    () => discrepancies.filter(discrepancyQuickFilterPredicate(quickFilter)),
    [discrepancies, quickFilter]
  );

  const quickFilterControl = (
    <SegmentedControl
      selectedId={quickFilter}
      onChange={({ detail }) => {
        setQuickFilter(detail.selectedId as DiscrepancyQuickFilter);
      }}
      label="Quick filter discrepancies"
      options={[
        { id: "all", text: `All (${discrepancies.length})` },
        {
          id: "failing",
          text: `Failing (${failingCount})`,
          disabled: failingCount === 0,
        },
      ]}
    />
  );

  return (
    <BundleAwareTable
      isBundle={isBundle}
      items={filteredDiscrepancies}
      columnDefinitions={discrepancyColumns(
        classifications,
        comparisonLabel,
        discrepancies.some((discrepancy) =>
          Boolean(discrepancy.returnId?.trim())
        )
      )}
      filterActions={quickFilterControl}
      initialPropertyFilter={initialPropertyFilter}
      onPropertyFilterChange={onPropertyFilterChange}
    />
  );
};

const matchSetColumns = (
  countryCode: string | undefined,
  classifications: Record<string, ClassificationEntry>,
  comparisonEnvironment: ComparisonEnvironment
): TableProps.ColumnDefinition<ShadowTestMatchSet>[] => [
  {
    id: "env",
    header: "Env",
    cell: (e) =>
      relabelComparisonEnvironment(e.environment, comparisonEnvironment) || "-",
    sortingField: "environment",
    minWidth: 90,
  },
  {
    id: "matchKey",
    header: "Match Key",
    cell: (e) => {
      if (!e.matchKey || !e.accountNumber || !e.environment) {
        return e.matchKey || "-";
      }
      const mpId = COUNTRY_TO_MARKETPLACE[countryCode || ""] || "ATVPDKIKX0DER";
      const stage =
        e.environment.toLowerCase() === "prod"
          ? Stage.Prod
          : comparisonEnvironment === ComparisonEnvironment.Beta
          ? Stage.Beta
          : Stage.Gamma;
      const url = createFlashUIMatcherLink(
        mpId,
        e.matchKey,
        stage,
        e.accountNumber
      );
      return (
        <Link href={url} external>
          {e.matchKey}
        </Link>
      );
    },
    sortingField: "matchKey",
    minWidth: 180,
  },
  {
    id: "account",
    header: "Account",
    cell: (e) => e.accountNumber || "-",
    sortingField: "accountNumber",
    minWidth: 140,
  },
  {
    id: "amount",
    header: "Balance",
    cell: (e) => (e.amount ? `${e.amount} ${e.currency || ""}` : "-"),
    sortingField: "amount",
    sortingComparator: numericFieldComparator(
      (e: ShadowTestMatchSet) => e.amount
    ),
    minWidth: 140,
  },
  {
    id: "closed",
    header: "Closed",
    cell: (e) => (
      <StatusIndicator type={e.closed ? "success" : "warning"}>
        {e.closed ? "Yes" : "No"}
      </StatusIndicator>
    ),
    sortingComparator: (a, b) => Number(a.closed ?? 0) - Number(b.closed ?? 0),
    minWidth: 100,
  },
  {
    id: "classification",
    header: "Classification",
    cell: (e) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
    sortingField: "classification",
    minWidth: 140,
  },
];

type MatchSetQuickFilter = "all" | "closed" | "open" | "failing";
const matchSetQuickFilterPredicate = (
  id: MatchSetQuickFilter
): ((matchSet: ShadowTestMatchSet) => boolean) => {
  switch (id) {
    case "closed":
      return (matchSet) => matchSet.closed === true;
    case "open":
      return (matchSet) => matchSet.closed !== true;
    case "failing":
      return (matchSet) => matchSet.passed === false;
    default:
      return () => true;
  }
};

export const MatchSetTable = ({
  matchSets,
  countryCode,
  classifications,
  comparisonEnvironment,
  initialPropertyFilter,
  onPropertyFilterChange,
}: {
  matchSets: ShadowTestMatchSet[];
  countryCode?: string;
  classifications: Record<string, ClassificationEntry>;
  comparisonEnvironment: ComparisonEnvironment;
  initialPropertyFilter?: PropertyFilterProps.Query;
  onPropertyFilterChange?: (query: PropertyFilterProps.Query) => void;
}) => {
  const [quickFilter, setQuickFilter] = useState<MatchSetQuickFilter>("all");
  const counts = useMemo(
    () => ({
      all: matchSets.length,
      closed: matchSets.filter(matchSetQuickFilterPredicate("closed")).length,
      open: matchSets.filter(matchSetQuickFilterPredicate("open")).length,
      failing: matchSets.filter(matchSetQuickFilterPredicate("failing")).length,
    }),
    [matchSets]
  );

  useEffect(() => {
    if (quickFilter !== "all" && counts[quickFilter] === 0) {
      setQuickFilter("all");
    }
  }, [counts, quickFilter]);

  const filteredMatchSets = useMemo(
    () => matchSets.filter(matchSetQuickFilterPredicate(quickFilter)),
    [matchSets, quickFilter]
  );

  const quickFilterControl = (
    <SegmentedControl
      selectedId={quickFilter}
      onChange={({ detail }) => {
        setQuickFilter(detail.selectedId as MatchSetQuickFilter);
      }}
      label="Quick filter match sets"
      options={[
        { id: "all", text: `All (${counts.all})` },
        {
          id: "closed",
          text: `Closed (${counts.closed})`,
          disabled: counts.closed === 0,
        },
        {
          id: "open",
          text: `Open / partial (${counts.open})`,
          disabled: counts.open === 0,
        },
        {
          id: "failing",
          text: `Failing (${counts.failing})`,
          disabled: counts.failing === 0,
        },
      ]}
    />
  );

  return (
    <BundleAwareTable
      items={filteredMatchSets}
      columnDefinitions={matchSetColumns(
        countryCode,
        classifications,
        comparisonEnvironment
      )}
      filterActions={quickFilterControl}
      initialPropertyFilter={initialPropertyFilter}
      onPropertyFilterChange={onPropertyFilterChange}
    />
  );
};
